import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";

import { modelOf } from '../../../../prop-types';
import AnalyticsService from '../../../../services/AnalyticsService';
import CheckoutStore from '../../../../store/CheckoutStore';
import CheckoutPageModule from '../../../../types/checkout/CheckoutPageModule';
import CheckoutModule from "../../common/CheckoutModule";

const CheckoutKCOModule = ({ analyticsService, checkoutStore, orderNumber, onOrderUpdate }) => {
  const kcoRef = useRef(null);
  const eventListenersRef = useRef([]);
  const { order } = checkoutStore;
  const { externalPaymentMethodAlerts } = order;

  // Handle KCO events and track analytics
  const handleKcoEvent = useCallback((eventData) => {
    const { event: eventName, data } = eventData;
    try {
      // Track analytics for the event - send only the event data as per KCO specs
      switch (eventName) {
        case 'load':
          // Data: { customer: { type: "person|organization" }, shipping_address: { country: "swe", postal_code: "16972" } }
          // Only send event if customer type has a value
          if (data?.customer?.type) {
            analyticsService.kcoLoad(data);
          }
          break;
        case 'customer':
          // Data: { type: "person|organization" }
          // Only send event if customer type has a value
          if (data?.type) {
            analyticsService.kcoCustomer(data);
          }
          break;
        case 'change':
          // Data: { email: "...", postal_code: "...", country: "..." }
          analyticsService.kcoChange(data);
          break;
        case 'billing_address_change':
          // Data: { postal_code: "75511", country: "aut" }
          analyticsService.kcoBillingAddressChange(data);
          break;
        case 'shipping_address_change':
          // Data: { postal_code: "75511", country: "aut" }
          analyticsService.kcoShippingAddressChange(data);
          break;
        case 'shipping_option_change':
          // Data: { description: "...", id: "...", name: "...", price: 99999, promo: "...", tax_amount: 7621, tax_rate: 825 }
          analyticsService.kcoShippingOptionChange(data);
          break;
        case 'order_total_change':
          // Data: { order_total: 6000 }
          analyticsService.kcoOrderTotalChange(data);
          break;
        case 'checkbox_change':
          // Data: { key: "additional_merchant_terms_checkbox", checked: true }
          analyticsService.kcoCheckboxChange(data);
          break;
        case 'network_error':
          // Data: {} (empty object)
          analyticsService.kcoNetworkError(data);
          break;
        case 'redirect_initiated':
          // Data: true
          analyticsService.kcoRedirectInitiated(data);
          break;
        case 'load_confirmation':
          // Data: {} (empty object)
          analyticsService.kcoLoadConfirmation(data);
          break;
      }
    } catch (error) {
      console.error(`Error handling KCO event ${eventName}:`, error);
    }
  }, [analyticsService, onOrderUpdate]);

  // Set up KCO event listeners
  const setupKcoEventListeners = useCallback(() => {
    if (!window._klarnaCheckout) {
      return;
    }

    // Don't set up listeners if cart is empty or order data is not available
    if (!order || !order.productList || order.cartIsEmpty) {
      return;
    }

    // All KCO events tracked for analytics
    const events = [
      'load',                        // KCO iframe loaded successfully
      'customer',                    // Organization type changed
      'change',                      // Postal code, country, or email changed
      'billing_address_change',      // Billing address submitted
      'shipping_address_change',     // Shipping address submitted
      'shipping_option_change',      // User selected shipping option
      'order_total_change',          // Cart changes from merchant
      'checkbox_change',             // Checkbox checked/unchecked
      'network_error',               // Network issue detected
      'redirect_initiated',          // User redirected to confirmation page
      'load_confirmation',           // Confirmation iframe loaded successfully
      'user_interacted',             // User started interacting with KCO iframe
    ];

    window._klarnaCheckout(function (api) {
      // Build event handlers object as per KCO documentation
      const eventHandlers = {};
      
      events.forEach(eventName => {
        eventHandlers[eventName] = (data) => handleKcoEvent({ event: eventName, data });
      });

      // Register all event handlers using correct KCO API syntax (object with event keys)
      api.on(eventHandlers);

      // Store API reference for cleanup
      eventListenersRef.current.push({ api });
    });
  }, [handleKcoEvent, analyticsService, order]);

  // Clean up event listeners
  const cleanupEventListeners = useCallback(() => {
    // KCO doesn't provide an explicit cleanup method
    // Event listeners are automatically cleaned up when the iframe is destroyed
    eventListenersRef.current = [];
  }, []);

  // Set up event listeners when KCO is available
  useEffect(() => {
    // Small delay to ensure KCO widget is fully loaded
    const timer = setTimeout(() => {
      setupKcoEventListeners();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanupEventListeners();
    };
  }, [setupKcoEventListeners, cleanupEventListeners, order.html_widget]);

  // Suspend KCO iframe when order updates are in progress
  useEffect(() => {
    if (checkoutStore.checkoutIsLoading) {
      suspendKCO();
    } else if (!checkoutStore.checkoutIsLoading) {
      resumeKCO();
    }
  }, [checkoutStore.checkoutIsLoading]);

  const suspendKCO = () => {
    if (window._klarnaCheckout) {
      window._klarnaCheckout(function (api) {
        api.suspend();
      });
    }
  };

  const resumeKCO = () => {
    if (window._klarnaCheckout) {
      window._klarnaCheckout(function (api) {
        api.resume();
      });
    }
  };

  const renderHtmlWidget = () => {
    // Don't render KCO widget if cart is empty or no widget HTML
    if (!order.html_widget || order.cartIsEmpty) {
      return null;
    }

    const widgetHtml = order.html_widget;
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = widgetHtml;

    return (
      <>
        <Helmet>
          {[].map.call(doc.getElementsByTagName('script'), (el, index) => (
            <script key={index}>{el.textContent}</script>
          ))}
        </Helmet>
        <CheckoutModule
          className="CheckoutKCOModule"
          generalAlerts={externalPaymentMethodAlerts.general}
          errorAlerts={externalPaymentMethodAlerts.errors}
          title={
            <FormattedMessage
              id="checkout.finalizeOrder"
              defaultMessage="Finalize the order"
            />
          }
          module={CheckoutPageModule.EXTERNAL_CHECKOUT_MODULE}
          orderNumber={orderNumber}
        >
          <div ref={kcoRef} dangerouslySetInnerHTML={{ __html: widgetHtml }} />
        </CheckoutModule>
      </>
    );
  };

  return (renderHtmlWidget());
};

CheckoutKCOModule.propTypes = {
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func,
  orderNumber: PropTypes.number.isRequired,
};

export default inject('analyticsService', 'checkoutStore')(observer(CheckoutKCOModule));

